'use client'

import { memo, useState } from 'react'
import type { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { ResearchAdCardHeader } from '@/features/research/research-ads/research-ad-card/research-ad-card-header'
import {
    ResearchAdCardFooter,
    ResearchAdCardMetrics,
} from '@/features/research/research-ads/research-ad-card/research-ad-card-metrics'
import { ResearchAdCardContent } from '@/features/research/research-ads/research-ad-card/research-ad-card-content'
import { Skeleton } from '@/components/ui/skeleton'
import { ResourceCard, RESOURCE_CARD_VIDEO_PLAY_MODE } from '@/components/resource-card/resource-card'
import { range } from '@/utils/functions/range'
import { cn } from '@/utils/styles/cn'
import { useResearchAdsContext } from '@/features/research/research-ads/research-ads-context'

interface ResearchAdCardProps {
    ad: IResearchedAd
    /** Opens the ad's preview — wired as the shared ResourceCard open activator. */
    onPreview?: () => void
    /** Bulk-selection wiring (the creatives-card pattern) — all four come from the grid. */
    selectionMode?: boolean
    selected?: boolean
    onToggleSelect?: () => void
    onEnterSelection?: () => void
}

export const ResearchAdCard = memo(
    ({ ad, onPreview, selectionMode = false, selected = false, onToggleSelect, onEnterSelection }: ResearchAdCardProps) => {
        const { config } = useResearchAdsContext()
        const [expandedVideo, setExpandedVideo] = useState<string | null>(null)
        const videoExpanded = expandedVideo === ad.uuid
        // Pass `undefined` (not a component that renders null) so ResourceCard skips the footer rule.
        const showFooter =
            !videoExpanded &&
            (!config.readOnly || Boolean(config.onRecreate || config.buildRecreateHref))

        return (
            <ResourceCard
                onOpen={onPreview}
                openLabel={onPreview ? `Open ${ad.creative.title ?? 'ad'}` : undefined}
                videoPlayMode={RESOURCE_CARD_VIDEO_PLAY_MODE.research}
                className={cn(
                    selected && 'ring-2 ring-orange-600/70',
                    selectionMode && !selected && 'hover:ring-2 hover:ring-orange-400'
                )}
                header={
                    <ResearchAdCardHeader
                        ad={ad}
                        selectionMode={selectionMode}
                        className={videoExpanded ? 'hidden' : undefined}
                    />
                }
                media={
                    <ResearchAdCardContent
                        ad={ad}
                        setExpandedVideo={setExpandedVideo}
                        onPreview={onPreview}
                        selectionMode={selectionMode}
                        selected={selected}
                        onToggleSelect={onToggleSelect}
                        onEnterSelection={onEnterSelection}
                    />
                }
                body={
                    <ResearchAdCardMetrics
                        ad={ad}
                        className={videoExpanded ? 'hidden' : undefined}
                    />
                }
                footer={
                    showFooter ? <ResearchAdCardFooter ad={ad} selectionMode={selectionMode} /> : undefined
                }
            />
        )
    }
)

ResearchAdCard.displayName = 'ResearchAdCard'

/**
 * @deprecated Prefer `ResourceCard` — kept so older call sites keep a named research wrapper.
 * Thin shell around the shared card chrome.
 */
export const ResearchAdCardContainer = ({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) => {
    return <ResourceCard className={className} body={<div className="contents">{children}</div>} />
}

/** Media heights cycled by index so the skeleton grid already reads as a masonry. */
const SKELETON_MEDIA_HEIGHTS = [340, 280, 400, 320, 380, 300] as const

export const ResearchAdCardSkeleton = ({
    index = 0,
    mediaAspectRatio,
}: {
    index?: number
    /**
     * Uniform media box (e.g. `"4 / 5"` — the real card's dimension-less fallback). Omit to cycle
     * masonry pixel heights. A 4-col shelf must pass this so every skeleton is the same height;
     * masonry heights would make the row as tall as the tallest card, then jump on swap.
     */
    mediaAspectRatio?: string
}) => {
    const { config } = useResearchAdsContext()
    const mediaHeight = SKELETON_MEDIA_HEIGHTS[index % SKELETON_MEDIA_HEIGHTS.length]
    // Mirror the real footer's shape per surface: read-only cards close with a single
    // right-aligned Recreate CTA, authenticated cards with Recreate plus the split save button.
    const hasSaveButton = !config.readOnly && config.allowSaveToBoard

    return (
        <ResourceCard
            className="w-full"
            header={
                <div className="flex items-center justify-between p-2">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-4 w-[60px]" />
                </div>
            }
            media={
                <Skeleton
                    className={cn(
                        'w-full rounded-none',
                        'relative overflow-hidden',
                        'after:absolute after:inset-0',
                        'after:-translate-x-full',
                        'after:animate-[shimmer_2s_infinite]',
                        'after:bg-linear-to-r',
                        'after:from-transparent after:via-white/10 after:to-transparent'
                    )}
                    style={mediaAspectRatio ? { aspectRatio: mediaAspectRatio } : { height: mediaHeight }}
                />
            }
            body={
                <div className="space-y-3 py-3">
                    <div className="flex items-center justify-between gap-2 px-3">
                        <Skeleton className="h-4 w-[65%]" />
                    </div>
                    <div className="grid grid-cols-3 gap-x-2 gap-y-2.5 px-3">
                        {range(6).map((i) => (
                            <div key={i} className="flex flex-col gap-1">
                                <Skeleton className="h-4 w-[80%]" />
                                <Skeleton className="h-2.5 w-[55%]" />
                            </div>
                        ))}
                    </div>
                </div>
            }
            footer={
                hasSaveButton ? (
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-7 w-[90px] shrink-0 rounded-[10px]" />
                        <Skeleton className="ml-auto h-7 w-[150px] rounded-[8px]" />
                    </div>
                ) : (
                    <div className="flex items-center">
                        <Skeleton className="ml-auto h-7 w-[110px] rounded-[8px]" />
                    </div>
                )
            }
        />
    )
}
